import { deepEquals, exists } from '@jack-henry/frontend-utils/functions';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SelectItemVm, TmFormControl } from './forms';

export type SegmentedControlTheme = 'primary' | 'secondary';

export const tagName = 'tm-segmented-control';
@customElement(tagName)
export class TmSegmentedControl<T = string> extends TmFormControl<SelectItemVm<T>> {
    @property({ attribute: false })
    public options: SelectItemVm<T>[] = [];

    @property({ attribute: false })
    public label = '';

    @property({ attribute: false })
    public controlTheme: SegmentedControlTheme = 'primary';

    @property({ type: Boolean, reflect: true })
    public fullWidth = false;

    public firstUpdated(changedProps: Map<PropertyKey, unknown>): void {
        if (!exists(this.initial)) {
            [this.initial] = this.options;
            this.changeValue(this.initial);
        }
        super.firstUpdated(changedProps);
    }

    protected validate(): boolean {
        return true;
    }

    protected updated(changedProperties: Map<PropertyKey, unknown>): void {
        super.updated(changedProperties);
        if (changedProperties.has('initial')) {
            this.changeValue(this.initial);
        }
    }

    protected render() {
        return html`<div>
            ${this.label
                ? html`<div class="ml-3 text-xs text-secondary">${this.label}:</div>`
                : nothing}
            <div
                class=${classMap({
                    'inline-flex border rounded-md border-brand': this.controlTheme === 'primary',
                    'flex flex-row rounded-lg p-[2px] bg-container-page':
                        this.controlTheme === 'secondary',
                    'w-full': this.fullWidth,
                })}
            >
                ${this.renderOptionItems()}
            </div>
        </div>`;
    }

    private renderLabel(option: SelectItemVm, selected: boolean, border: boolean) {
        const primary = {
            'inline-block py-[2px] min-w-28 text-sm text-center capitalize text-brand': true,
            'basis-1/2 py-1 text-sm text-center': this.fullWidth,
            'px-2': !this.fullWidth,
            relative: selected,
            'z-10': selected,
            'bg-brand': selected,
            '-m-[2px]': selected,
            'py-[4px]': selected,
            'rounded-lg': selected,
            'text-light': selected,
            'border-x': border && !selected,
        };

        const secondary = {
            'basis-1/2 py-1 text-sm text-center': true,
            relative: selected,
            'z-10': selected,
            'bg-container-primary': selected,
            'rounded-md': selected,
            'font-semibold': selected,
            'shadow-md': selected,
            'opacity-25': option.disabled || false,
        };

        return html`<label
            class=${classMap(this.controlTheme === 'secondary' ? secondary : primary)}
            for=${ifDefined(option.value)}
        >
            ${option.label}
        </label>`;
    }

    private renderOptionItems() {
        return this.options.map((option: SelectItemVm<T>, i) => {
            const selected = deepEquals(option, this.value);
            const border = i !== 0 && i !== this.options.length - 1;
            return html`<input
                    class="hidden appearance-none"
                    .id=${option.value}
                    name="options"
                    type="radio"
                    ?checked=${selected}
                    ?disabled=${option.disabled}
                    @click=${() => {
                        this.changeValue(option);
                    }}
                />
                ${this.renderLabel(option, selected, border)}`;
        });
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmSegmentedControl;
    }
}
